import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, combineLatest, map, of, switchMap, tap } from 'rxjs';

import {
  CareServices,
  formatUsd,
  getInsuranceEstimate,
  getPlansForService,
  getSelfPayEstimate,
  IPlanOption,
  IStateData,
  ServiceValue
} from '@sondermind/cost-estimate-core';
import { CostEstimateStore } from '@sondermind/data-access/client-facing/cost-estimate';
import { DirectoryLinkService } from '@sondermind/data-access/directory-link';
import { ProviderNetworkCompanyStore } from '@sondermind/data-access/provider-network-company';
import { Logger } from '@sondermind/datadog-logging';
import { FilterCategory, FilterSelectV2Component, IOptionCategory } from '@sondermind/features/filter-form';
import { IrisButtonModule, IrisButtonTypes } from '@sondermind/iris/button';
import { DropdownOption } from '@sondermind/iris/dropdown';
import { IconSizes, IrisIconComponent } from '@sondermind/iris/icon';
import { IrisButtonTypeAttributeOptions } from '@sondermind/iris/shared';
import {
  CASH_PAY_OPTION_VALUE,
  IInsurance,
  IInsuranceOptionValue,
  InsuranceCategoryName,
  InsuranceType
} from '@sondermind/utilities/models-provider-network-company';
import { IProviderShareableProfile } from '@sondermind/utilities/models-public-facing';

import { PSYCHIATRIST_EDUCATION_LEVELS } from '@sondermind/constants';

import { getValidStates } from '../../utils/valid-states.util';

const NO_STATE_TEXT = 'Select a state first';
const LOADING_TEXT = 'Loading…';
const SELECT_INSURANCE_TEXT = 'Select insurance';
const CASH_PAY_OPTION_NAME = 'I\'ll pay out of pocket';

interface ICostEstimateForm {
  state: FormControl<string | null>;
  insurance: FormControl<IInsuranceOptionValue | null>;
}

/** The values the estimate panel renders. `insuranceTitle` goes unread on the self-pay variant. */
interface ICostEstimateVm {
  formattedCost: string;
  isSelfPay: boolean;
  insuranceTitle: string;
  stateName: string;
  service: ServiceValue;
}

/** Everything one state's lookup yields: the plans the provider is in network with, and its pricing. */
interface IStatePlans {
  insurances: IInsurance[];
  stateData: IStateData | null;
}

/**
 * Which set of copy the card renders. The number doubles as the footnote's superscript marker, and the
 * copy for each variant lives in the template.
 */
enum CostEstimateVariant {
  COMMON_COPAY = 1,
  INSURANCE_ESTIMATE = 2,
  SELF_PAY_ESTIMATE = 3,
}

@Component({
  selector: 'cost-estimate-psl',
  templateUrl: './cost-estimate-psl.component.html',
  styleUrls: ['./cost-estimate-psl.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, FilterSelectV2Component, IrisButtonModule, IrisIconComponent],
})
export class CostEstimatePslComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly logger = inject(Logger);
  private readonly providerNetworkCompanyStore = inject(ProviderNetworkCompanyStore);
  private readonly costEstimateStore = inject(CostEstimateStore);
  private readonly directoryLinkService = inject(DirectoryLinkService);

  readonly providerShareableProfile = input.required<IProviderShareableProfile>();

  /**
   * Booking is the page's to open — it needs the profile, address, share code, and native-app context
   * the parent holds — so this reports the click rather than handling it, the same way the header,
   * connect, and actions cards do.
   */
  readonly requestASessionClicked = output();

  protected readonly irisButtonTypes = IrisButtonTypes;
  protected readonly irisButtonTypeAttributes = IrisButtonTypeAttributeOptions;
  protected readonly costEstimateVariants = CostEstimateVariant;
  protected readonly iconSizes = IconSizes;

  protected readonly form = new FormGroup<ICostEstimateForm>({
    state: new FormControl<string | null>(null, Validators.required),
    insurance: new FormControl<IInsuranceOptionValue | null>(null, Validators.required),
  });

  protected readonly isPsychiatrist = computed(
    () => PSYCHIATRIST_EDUCATION_LEVELS.includes(this.providerShareableProfile().education_level)
  );

  /**
   * Every state the provider is licensed in — the same active-licensure intersection the
   * "Licensed in" block renders (MER-5032). Named for display, valued by abbreviation so the
   * selection lines up with `IProviderNetworkCompany.network_state`.
   */
  protected readonly stateOptions = computed<Array<IOptionCategory<string>>>(() => [{
    name: FilterCategory.STATE,
    options: getValidStates(this.providerShareableProfile())
      .map((state) => ({ name: state.name, value: state.abbrev }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  }]);

  private readonly selectedState = toSignal(this.form.controls.state.valueChanges);
  private readonly selectedInsurance = toSignal(this.form.controls.insurance.valueChanges);

  /** Cash pay carries its own disclaimer; every insurance-backed plan shares the other. */
  protected readonly variant = computed<CostEstimateVariant>(() => {
    const insurance = this.selectedInsurance();
    if (!insurance) return CostEstimateVariant.COMMON_COPAY;

    return insurance.type === InsuranceType.CASH
      ? CostEstimateVariant.SELF_PAY_ESTIMATE
      : CostEstimateVariant.INSURANCE_ESTIMATE;
  });

  /** The service this provider is booked for, which decides which of a plan's two rates applies. */
  private readonly service = computed<ServiceValue>(
    () => (this.isPsychiatrist() ? CareServices.Psychiatry.value : CareServices.Therapy.value)
  );

  private readonly stateInsurances = signal<IInsurance[]>([]);
  private readonly stateData = signal<IStateData | null>(null);
  private readonly isLoadingPlans = signal<boolean>(false);
  private readonly hasSubmitted = signal<boolean>(false);

  /**
   * The selected state's plans that apply to this provider's service, keyed by provider-network-company
   * id. A plan that doesn't cover the service is absent rather than priced, since `getInsuranceEstimate`
   * hands back the raw rate without checking whether the plan covers it.
   */
  private readonly servicePlans = computed<Map<number, IPlanOption>>(() => new Map<number, IPlanOption>(
    getPlansForService(this.stateData()?.plans ?? [], this.service()).map((plan) => [plan.id, plan])
  ));

  /** Plans the provider accepts in the selected state, grouped the way the directory filters group them. */
  protected readonly insuranceOptions = computed<Array<IOptionCategory<IInsuranceOptionValue>>>(() => {
    const state = this.selectedState();

    return state ? this.toCategorizedOptions(this.stateInsurances(), state) : [];
  });

  protected readonly insuranceEmptyText = computed<string>(() => {
    if (!this.selectedState()) return NO_STATE_TEXT;

    return this.isLoadingPlans() ? LOADING_TEXT : SELECT_INSURANCE_TEXT;
  });

  /**
   * True once the client has asked a complete question. Both panel variants hang off this: the submit
   * button gives way to the auto-update hint, and the card grows a flat bottom edge to meet the panel.
   * A state change clears the plan, so an incomplete pair means the client owes us another answer.
   */
  protected readonly isEstimateRequested = computed<boolean>(
    () => this.hasSubmitted() && !!this.selectedState() && !!this.selectedInsurance()
  );

  /**
   * The figure to render, or null when there isn't one — which the template reads as the empty state,
   * since it only asks once `isEstimateRequested`. Everything it needs already arrived with the state,
   * so this is a plain derivation rather than a request: changing either dropdown re-prices
   * immediately, which is what the card promises once the panel is up.
   */
  protected readonly estimate = computed<ICostEstimateVm | null>(() => {
    const stateAbbrev = this.selectedState();
    const insurance = this.selectedInsurance();
    if (!this.hasSubmitted() || !stateAbbrev || !insurance) return null;

    const service = this.service();
    const isSelfPay = insurance.type === InsuranceType.CASH;
    const { cost } = isSelfPay
      ? getSelfPayEstimate(this.stateData()?.selfPay, service)
      : getInsuranceEstimate(this.servicePlans().get(insurance.pncId), service);

    if (!this.hasCost(cost)) return null;

    return {
      formattedCost: formatUsd(cost),
      isSelfPay,
      insuranceTitle: insurance.title,
      stateName: this.stateNameFor(stateAbbrev),
      service
    };
  });

  /**
   * The directory, pre-filtered by what the client just answered, so "other providers" means others who
   * fit the same estimate. It's a separate app at its own origin, so this is an absolute href rather
   * than a `routerLink`. Cash pay isn't one of the directory's insurance filters, so it narrows by state
   * alone.
   */
  protected readonly browseOtherProvidersUrl = computed<string>(() => {
    const insurance = this.selectedInsurance();
    const insuranceTitle = insurance && insurance.type !== InsuranceType.CASH
      ? insurance.title
      : undefined;

    return this.directoryLinkService.buildProviderDirectoryLink(
      this.selectedState() ?? undefined,
      undefined,
      insuranceTitle
    );
  });

  ngOnInit(): void {
    this.form.controls.state.valueChanges.pipe(
      tap(() => {
        // A plan is only valid within the state it was listed under, so switching state drops it.
        this.form.controls.insurance.setValue(null);
        this.stateInsurances.set([]);
        this.stateData.set(null);
        this.isLoadingPlans.set(!!this.form.controls.state.value);
      }),
      switchMap((state) => {
        if (!state) return of<IStatePlans>({ insurances: [], stateData: null });

        // One fills the dropdown, the other prices whatever gets picked. Pricing degrades on its own so
        // an outage costs the client the figure, not the whole question.
        return combineLatest([
          this.providerNetworkCompanyStore.getInsuranceOptionsByState$({ state }),
          this.costEstimateStore.getStateData$(state).pipe(
            catchError((err: unknown) => {
              this.logger.error('Failed to load cost-estimate pricing', { err });
              return of<IStateData | null>(null);
            })
          )
        ]).pipe(
          map(([insurances, stateData]): IStatePlans => ({ insurances, stateData })),
          catchError((err: unknown) => {
            this.logger.error('Failed to load cost-estimate insurance options', { err });
            return of<IStatePlans>({ insurances: [], stateData: null });
          })
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ insurances, stateData }) => {
      this.stateInsurances.set(insurances);
      this.stateData.set(stateData);
      this.isLoadingPlans.set(false);
    });
  }

  /**
   * Opens the estimate. Only the first submit matters — from then on `estimate` tracks the dropdowns
   * directly, so the button is replaced by the auto-update hint.
   */
  protected onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.hasSubmitted.set(true);
  }

  /** A rate we can actually put on screen. Core leaves `cost` unset when it has nothing to quote. */
  private hasCost(cost: number | string | null | undefined): cost is number | string {
    return cost !== null && cost !== undefined && cost !== '';
  }

  /** The disclaimer names the state, so fall back to the abbreviation rather than an empty phrase. */
  private stateNameFor(abbrev: string): string {
    const [states] = this.stateOptions();

    return states.options.find((option) => option.value === abbrev)?.name ?? abbrev;
  }

  /**
   * Splits the state's plans into the same categories the directory's filter dropdown uses, keeping
   * only the ones this provider is in network with. Cash pay leads the list in an unnamed group, so
   * it reads as the default rather than as one more plan.
   */
  private toCategorizedOptions(
    insurances: IInsurance[],
    state: string
  ): Array<IOptionCategory<IInsuranceOptionValue>> {
    const acceptedPncIds = new Set(
      (this.providerShareableProfile().provider_network_companies ?? [])
        .filter((pnc) => pnc.network_state === state)
        .map((pnc) => pnc.id)
    );
    const cashPay: IOptionCategory<IInsuranceOptionValue> = {
      name: '',
      options: [{ name: CASH_PAY_OPTION_NAME, value: CASH_PAY_OPTION_VALUE }]
    };
    const medicare: IOptionCategory<IInsuranceOptionValue> = {
      name: InsuranceCategoryName.MEDICARE,
      options: []
    };
    const medicaid: IOptionCategory<IInsuranceOptionValue> = {
      name: InsuranceCategoryName.MEDICAID,
      options: []
    };
    const insurance: IOptionCategory<IInsuranceOptionValue> = {
      name: InsuranceCategoryName.INSURANCE,
      options: []
    };
    const eap: IOptionCategory<IInsuranceOptionValue> = {
      name: InsuranceCategoryName.EAP,
      options: []
    };

    insurances
      // Every plan the provider accepts is offered, even one we can't price — the profile already lists
      // it as accepted, so hiding it here would read as a contradiction. An unpriceable pick answers
      // with the empty state instead.
      .filter((ins) => acceptedPncIds.has(ins.pncId))
      .forEach((ins) => {
        // One plan may belong to several categories.
        if (ins.isMedicare) medicare.options.push(this.toOption(ins, InsuranceType.MEDICARE));
        if (ins.isMedicaid) medicaid.options.push(this.toOption(ins, InsuranceType.MEDICAID));
        if (ins.isPrivateInsurance) insurance.options.push(this.toOption(ins, InsuranceType.INSURANCE));
        if (ins.isEap) eap.options.push(this.toOption(ins, InsuranceType.EAP));
      });

    return [cashPay, medicare, medicaid, insurance, eap];
  }

  private toOption(insurance: IInsurance, type: InsuranceType): DropdownOption<IInsuranceOptionValue> {
    return {
      name: insurance.title,
      value: {
        pncId: insurance.pncId,
        title: insurance.title,
        type,
        tag: insurance.tag
      }
    };
  }
}
